import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { resetAgentSetupStatus } from '@/composables/useAgentSetupStatus'
import {
  PROFILE_REQUEST,
  profileAttributes,
  profileVoiceSet,
  voiceFromProfile
} from '@/composables/useAgentVoiceSettings'
import {
  AGENT_STEP_KEYS,
  applyStarters,
  defaultWizardData,
  FALLBACK_STORE_NAME,
  STEP_AGENT_TYPES,
  stepCoveredByExisting,
  stepForKey,
  stepValid,
  STORE_STEP_KEYS,
  visibleSteps
} from '@/config/agentWizardSteps'
import { buildSetupPayload } from '@/helpers/agentWizardPayload'
import { clearDraft, isPlainObject, mergeDraft, readDraft, writeDraft } from '@/helpers/wizardDraft'
import api from '@/store/api'
import { mapTypeIncludingInactive } from '@/store/mappers'
import { AGENTS_REQUEST, withoutCateringAgents } from '@/view/ai/agentTypes'

const GENERIC_ERROR = 'Something went wrong creating your agents. Nothing was created.'
const LOAD_ERROR = 'Your store details could not be loaded. Try again.'
const FIRST_STEP = 'voice'
const REVIEW_STEP = 'review'
const NO_STORES = []

const filled = value => Boolean(value && String(value).trim())

const serverErrors = error => {
  const messages = error?.response?.data?.errors
  return Array.isArray(messages) && messages.length ? messages : [GENERIC_ERROR]
}

const emptyExisting = () => ({ winback: false, reviews: false, carts: new Set(), upsell: new Set() })

// What the merchant already has, from the agents in the store, active or not (catering agents are not part of this wizard).
const existingFromStore = () => {
  const playbooks = new Map(mapTypeIncludingInactive('agent_playbook').map(playbook => [String(playbook.id), playbook]))
  const existing = emptyExisting()
  withoutCateringAgents(mapTypeIncludingInactive('agent')).forEach(agent => {
    const stepKey = AGENT_STEP_KEYS.find(key => STEP_AGENT_TYPES[key] === agent.attributes?.type)
    if (!stepKey) return
    if (!STORE_STEP_KEYS.includes(stepKey)) {
      existing[stepKey] = true
      return
    }
    const playbookId = agent.relationships?.agent_playbook?.data?.id
    const storeId = playbooks.get(String(playbookId))?.attributes?.belongs_to_id
    if (storeId !== null && storeId !== undefined) existing[stepKey].add(String(storeId))
  })
  return existing
}

export function useAgentWizard() {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const draftKey = `agentWizard:${store.getters.loggedInUserId}`

  const draft = readDraft(draftKey)
  const data = reactive(mergeDraft(defaultWizardData(), draft?.data))
  // A voice or about saved in the draft wins over the profile.
  let draftHasVoice = isPlainObject(draft?.data?.voice) || isPlainObject(draft?.data?.about)

  const loading = ref(true)
  const loadFailed = ref(false)
  const status = ref(null)
  const existing = ref(emptyExisting())
  const stores = computed(() => status.value?.stores ?? NO_STORES)
  const ctx = reactive({
    storeName: computed(() => stores.value[0]?.store_name || FALLBACK_STORE_NAME),
    storeCount: computed(() => stores.value.length)
  })

  const createdAgentIds = ref(Array.isArray(draft?.createdAgentIds) ? draft.createdAgentIds.map(String) : [])
  const created = ref(createdAgentIds.value.length > 0)
  const saving = ref(false)
  const errors = ref([])

  // The URL wins as a pair; the draft's step and store are only used together when the URL names no step.
  const urlStep = stepForKey(route.params.step)?.key
  const requestedStep = urlStep ?? stepForKey(draft?.stepKey)?.key ?? FIRST_STEP
  const requestedStore = urlStep ? route.params.store ?? null : draft?.storeKey ?? null
  const stepKey = ref(created.value ? REVIEW_STEP : requestedStep)
  const storeKey = ref(!created.value && requestedStore !== null ? String(requestedStore) : null)

  // steps and rows

  const steps = computed(() => visibleSteps())

  const stepSkipped = key => {
    if (!AGENT_STEP_KEYS.includes(key)) return false
    return stepCoveredByExisting(key, existing.value, stores.value)
  }

  const storeLabel = storeId => stores.value.find(s => String(s.id) === String(storeId))?.store_name ?? ''

  const hasSubSteps = key =>
    STORE_STEP_KEYS.includes(key) && data[key].enabled && data[key].per_store && ctx.storeCount > 1

  const rows = computed(() =>
    steps.value.flatMap(step => {
      const parent = { ...step, skipped: stepSkipped(step.key) }
      if (parent.skipped || !hasSubSteps(step.key)) return [parent]
      const children = data[step.key].store_ids.map(id => ({
        key: step.key,
        storeId: String(id),
        label: storeLabel(id),
        index: step.index,
        skipped: false
      }))
      return [parent, ...children]
    })
  )

  const sameRow = (row, key, storeId) => row.key === key && (row.storeId ?? null) === (storeId ?? null)

  const step = computed(
    () =>
      rows.value.find(row => sameRow(row, stepKey.value, storeKey.value)) ??
      rows.value.find(row => sameRow(row, stepKey.value, null)) ??
      rows.value[0]
  )

  // validity

  const stepIsValid = key => stepSkipped(key) || stepValid(key, data, ctx)

  const storeStepFilledIn = row => {
    if (!row.storeId) {
      const shared = data[row.key]
      return !shared.enabled || (hasSubSteps(row.key) ? shared.store_ids.length > 0 : stepValid(row.key, data, ctx))
    }
    return filled(data[row.key].stores[row.storeId]?.message)
  }

  const rowValid = row => (row.storeId || hasSubSteps(row.key) ? storeStepFilledIn(row) : stepIsValid(row.key))
  const valid = computed(() => Boolean(step.value) && rowValid(step.value))
  // Same rule as allStepsValid, except steps for agents the merchant already has count as done.
  const canReview = computed(() => steps.value.every(({ key }) => stepIsValid(key)))

  // A single store has no sub-steps to fill per-store messages in, so per_store must not stay on.
  const normalizePerStore = () => {
    if (!status.value || ctx.storeCount > 1) return
    STORE_STEP_KEYS.forEach(key => {
      data[key].per_store = false
    })
  }
  watch([() => ctx.storeCount, () => data.carts.per_store, () => data.upsell.per_store], normalizePerStore, {
    flush: 'sync'
  })

  // persistence

  const persist = () => {
    if (loading.value || loadFailed.value) return
    writeDraft(draftKey, {
      data: JSON.parse(JSON.stringify(data)),
      stepKey: stepKey.value,
      storeKey: storeKey.value,
      createdAgentIds: createdAgentIds.value
    })
  }
  watch(data, persist, { deep: true })

  // navigation

  const showStep = (key, storeId = null) => {
    stepKey.value = key
    storeKey.value = storeId === null ? null : String(storeId)
    errors.value = []
    router.replace({ params: { step: key, store: storeKey.value ?? undefined }, query: route.query })
    persist()
  }

  const go = (key, storeId = null) => {
    const target = rows.value.find(row => sameRow(row, key, storeId === null ? null : String(storeId)))
    if (!target) return
    if (sameRow(target, stepKey.value, storeKey.value)) return
    if (key === REVIEW_STEP && !canReview.value) return
    showStep(key, target.storeId ?? null)
  }

  const neighbour = direction => {
    const current = rows.value.findIndex(row => row === step.value)
    const candidates = direction > 0 ? rows.value.slice(current + 1) : rows.value.slice(0, current).reverse()
    return candidates.find(row => !row.skipped)
  }

  const next = () => {
    if (!valid.value) return
    const following = neighbour(1)
    if (following) go(following.key, following.storeId ?? null)
  }

  const back = () => {
    const previous = neighbour(-1)
    if (previous) go(previous.key, previous.storeId ?? null)
  }

  const firstUnfinishedStep = () => steps.value.find(s => s.key !== REVIEW_STEP && !stepIsValid(s.key))?.key

  // loading

  // Store ids that still exist and have no agent of this step yet.
  const openStoreIds = (key, ids) => {
    const current = new Set(stores.value.map(s => String(s.id)))
    return ids.map(String).filter(id => current.has(id) && !existing.value[key].has(id))
  }

  // A stale or two-tab draft can hold stores that are gone or now covered by an existing agent.
  const pruneStoreChoices = () => {
    STORE_STEP_KEYS.forEach(key => {
      const step = data[key]
      step.store_ids = openStoreIds(key, step.store_ids)
      const kept = new Set(step.store_ids)
      step.stores = Object.fromEntries(Object.entries(step.stores).filter(([id]) => kept.has(id)))
    })
  }

  const fillStoreDefaults = () => {
    STORE_STEP_KEYS.forEach(key => {
      if (data[key].store_ids.length === 0)
        data[key].store_ids = openStoreIds(
          key,
          stores.value.map(s => s.id)
        )
    })
  }

  const loadExisting = async () => {
    await api.call('GET', AGENTS_REQUEST)
    existing.value = existingFromStore()
  }

  const prefillVoice = attributes => {
    if (draftHasVoice || !profileVoiceSet(attributes)) return
    const { additional_context, ...voice } = voiceFromProfile(attributes)
    Object.assign(data.voice, voice)
    data.about.additional_context = additional_context
  }

  const fetchAll = async () => {
    const response = await api.call('GET', 'agents/setup_status', {}, { receive: false, save: false })
    const [profile] = await Promise.all([api.call('GET', PROFILE_REQUEST), loadExisting()])
    status.value = response.data.data
    return profileAttributes(profile)
  }

  // A draft that remembers created agents is only worth keeping while at least one of them still exists.
  const createdAgentsStillExist = () => {
    const ids = new Set(mapTypeIncludingInactive('agent').map(agent => String(agent.id)))
    return createdAgentIds.value.some(id => ids.has(id))
  }

  // Back to an empty first step: the draft is gone and the next load prefills from the profile again.
  const startFresh = () => {
    clearDraft(draftKey)
    Object.assign(data, defaultWizardData())
    draftHasVoice = false
    createdAgentIds.value = []
    created.value = false
    stepKey.value = FIRST_STEP
    storeKey.value = null
    router.replace({ params: { step: FIRST_STEP, store: undefined }, query: route.query })
  }

  // On a failed load nothing is applied or saved: the shell shows the error and a Retry button.
  const load = async () => {
    loading.value = true
    loadFailed.value = false
    errors.value = []
    let profile
    try {
      profile = await fetchAll()
    } catch {
      loadFailed.value = true
      errors.value = [LOAD_ERROR]
      loading.value = false
      return
    }
    if (created.value && !createdAgentsStillExist()) startFresh()
    normalizePerStore()
    prefillVoice(profile)
    applyStarters(data, ctx.storeName)
    pruneStoreChoices()
    fillStoreDefaults()
    loading.value = false
    if (stepKey.value === REVIEW_STEP && !created.value && !canReview.value)
      showStep(firstUnfinishedStep() ?? REVIEW_STEP)
    else if (created.value && route.params.step !== REVIEW_STEP) showStep(REVIEW_STEP)
    else persist()
  }
  load()

  // submit and reset

  // Agents and stores the merchant already has are never sent again.
  const payloadData = () => {
    const copy = JSON.parse(JSON.stringify(data))
    AGENT_STEP_KEYS.forEach(key => {
      if (stepSkipped(key)) copy[key].enabled = false
    })
    STORE_STEP_KEYS.forEach(key => {
      copy[key].store_ids = openStoreIds(key, copy[key].store_ids)
    })
    return copy
  }

  const submit = async () => {
    if (created.value) return createdAgentIds.value
    if (saving.value || loading.value || loadFailed.value) return null
    saving.value = true
    errors.value = []
    try {
      const response = await api.call('POST', 'agents/setup', buildSetupPayload(payloadData()))
      createdAgentIds.value = response.data.data.map(row => String(row.id))
      created.value = true
      stepKey.value = REVIEW_STEP
      storeKey.value = null
      persist()
      resetAgentSetupStatus()
      return createdAgentIds.value
    } catch (error) {
      errors.value = serverErrors(error)
      return null
    } finally {
      saving.value = false
    }
  }

  // Start over: a fresh draft, loaded again so the stores, existing agents and saved voice are current.
  const reset = async () => {
    startFresh()
    await load()
  }

  // Done after creation: the agents live on the agents pages now, so the wizard forgets them.
  const finish = () => {
    clearDraft(draftKey)
    createdAgentIds.value = []
    created.value = false
  }

  return {
    data,
    steps,
    rows,
    stepKey,
    storeKey,
    step,
    valid,
    canReview,
    next,
    back,
    go,
    reset,
    finish,
    submit,
    saving,
    errors,
    loading,
    loadFailed,
    retry: load,
    status,
    stores,
    existing,
    created,
    createdAgentIds,
    ctx
  }
}
